import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const MARGIN_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const STRATEGIC_OPTIONS = [
  { value: 'strategic', label: 'Strategic' },
  { value: 'standard', label: 'Standard' },
  { value: 'sunset', label: 'Sunset' },
];

const CATEGORY_COLORS = {
  networking: 'bg-blue-500/10 text-blue-600',
  security: 'bg-red-500/10 text-red-600',
  cloud: 'bg-purple-500/10 text-purple-600',
  software: 'bg-green-500/10 text-green-600',
  telecom: 'bg-orange-500/10 text-orange-600',
  iot: 'bg-cyan-500/10 text-cyan-600',
  hardware: 'bg-slate-500/10 text-slate-600',
};

const STRATEGIC_COLORS = {
  strategic: 'bg-success/10 text-success border-success',
  standard: 'bg-primary/10 text-primary border-primary',
  sunset: 'bg-warning/10 text-warning border-warning',
};

const EMPTY_FORM = {
  catalogProductId: '',
  marginCategory: 'medium',
  strategicMarking: 'standard',
  priceRange: '',
  warrantySupport: '',
  supportedUseCases: '',
};

const ProductsTable = ({ products, catalogProducts = [], onAddProduct, onEditProduct, onDeleteProduct, onMarkStrategic }) => {
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Build catalog options for dropdown
  const catalogOptions = catalogProducts?.map(p => ({
    value: p?.id,
    label: `${p?.productName}${p?.oemBrand ? ` — ${p?.oemBrand}` : ''}${p?.modelSku ? ` (${p?.modelSku})` : ''}`,
  }));

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-fill fields from catalog when product is selected
      if (field === 'catalogProductId' && value) {
        const catalogItem = catalogProducts?.find(p => p?.id === value);
        if (catalogItem) {
          updated.marginCategory = catalogItem?.marginCategory || 'medium';
          updated.priceRange = catalogItem?.priceRange || '';
        }
      }
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!formData?.catalogProductId && !editingId) return;
    const catalogItem = catalogProducts?.find(p => p?.id === formData?.catalogProductId);
    const payload = {
      // Catalog-sourced fields
      productName: catalogItem?.productName || formData?.productName,
      category: catalogItem?.category || formData?.category,
      oemBrand: catalogItem?.oemBrand || formData?.oemBrand,
      modelSku: catalogItem?.modelSku || formData?.modelSku,
      licensingModel: catalogItem?.licensingModel || formData?.licensingModel,
      targetIndustry: catalogItem?.targetIndustry || formData?.targetIndustry,
      // Partner-specific overrides
      priceRange: formData?.priceRange || catalogItem?.priceRange,
      marginCategory: formData?.marginCategory,
      strategicMarking: formData?.strategicMarking,
      warrantySupport: formData?.warrantySupport,
      supportedUseCases: formData?.supportedUseCases,
      catalogProductId: formData?.catalogProductId,
    };
    if (editingId) {
      onEditProduct(editingId, payload);
      setEditingId(null);
    } else {
      onAddProduct(payload);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setIsAddFormOpen(false);
    setEditingId(null);
  };

  const handleEdit = (product) => {
    setFormData({
      catalogProductId: product?.catalogProductId || '',
      marginCategory: product?.marginCategory || 'medium',
      strategicMarking: product?.strategicMarking || 'standard',
      priceRange: product?.priceRange || '',
      warrantySupport: product?.warrantySupport || '',
      supportedUseCases: product?.supportedUseCases || '',
    });
    setEditingId(product?.id);
    setIsAddFormOpen(true);
  };

  const getCategoryColor = (category) => CATEGORY_COLORS?.[category] || 'bg-muted text-muted-foreground';
  const getStrategicColor = (marking) => STRATEGIC_COLORS?.[marking] || 'bg-muted text-muted-foreground border-border';

  return (
    <div className="space-y-16 p-16">
      {/* Catalog notice banner */}
      <div className="flex items-start gap-10 p-12 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
        <Icon name="Info" size={16} className="shrink-0 mt-1" />
        <span>Products are selected from the <strong>Master Products Catalog</strong> — no free-text entry. This ensures consistent naming across all partners.</span>
      </div>
      <div className="flex items-center justify-between gap-12 flex-wrap">
        <Button
          variant={isAddFormOpen ? 'outline' : 'default'}
          iconName={isAddFormOpen ? 'X' : 'Plus'}
          iconPosition="left"
          iconSize={18}
          onClick={() => { setIsAddFormOpen(!isAddFormOpen); if (isAddFormOpen) resetForm(); }}
        >
          {isAddFormOpen ? 'Cancel' : 'Add Product from Catalog'}
        </Button>
      </div>
      {isAddFormOpen && (
        <div className="bg-card border border-border rounded-lg p-16 md:p-24 shadow-elevation-sm">
          <h3 className="text-lg font-semibold text-foreground mb-16">
            {editingId ? 'Edit Product Mapping' : 'Add Product from Catalog'}
          </h3>

          {catalogOptions?.length === 0 ? (
            <div className="flex items-center gap-10 p-12 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 mb-16">
              <Icon name="AlertTriangle" size={16} />
              <span>No products in the master catalog yet. Ask an admin to add products to the catalog first.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-3">
                <Select
                  label="Select Product from Catalog *"
                  options={catalogOptions}
                  value={formData?.catalogProductId}
                  onChange={(value) => handleInputChange('catalogProductId', value)}
                  placeholder="Search and select a product..."
                  required
                />
                {formData?.catalogProductId && (() => {
                  const item = catalogProducts?.find(p => p?.id === formData?.catalogProductId);
                  return item ? (
                    <div className="mt-8 p-10 bg-slate-50 rounded-lg text-xs text-slate-600 flex flex-wrap gap-12">
                      {item?.category && <span><strong>Category:</strong> {item?.category}</span>}
                      {item?.oemBrand && <span><strong>Brand:</strong> {item?.oemBrand}</span>}
                      {item?.modelSku && <span><strong>SKU:</strong> {item?.modelSku}</span>}
                      {item?.licensingModel && <span><strong>Licensing:</strong> {item?.licensingModel}</span>}
                      {item?.targetIndustry && <span><strong>Industry:</strong> {item?.targetIndustry}</span>}
                    </div>
                  ) : null;
                })()}
              </div>
              <Select
                label="Margin Category"
                options={MARGIN_OPTIONS}
                value={formData?.marginCategory}
                onChange={(value) => handleInputChange('marginCategory', value)}
              />
              <Select
                label="Strategic Marking"
                options={STRATEGIC_OPTIONS}
                value={formData?.strategicMarking}
                onChange={(value) => handleInputChange('strategicMarking', value)}
              />
              <Input
                label="Price Range (Partner)"
                type="text"
                placeholder="e.g., OMR 1,000–OMR 5,000"
                value={formData?.priceRange}
                onChange={(e) => handleInputChange('priceRange', e?.target?.value)}
              />
              <Input
                label="Warranty / Support Model"
                type="text"
                placeholder="Enter warranty details"
                value={formData?.warrantySupport}
                onChange={(e) => handleInputChange('warrantySupport', e?.target?.value)}
              />
              <Input
                label="Supported Use Cases"
                type="text"
                placeholder="Enter use cases"
                value={formData?.supportedUseCases}
                onChange={(e) => handleInputChange('supportedUseCases', e?.target?.value)}
              />
            </div>
          )}

          <div className="flex items-center gap-12 mt-16">
            <Button variant="default" onClick={handleSubmit} disabled={!formData?.catalogProductId && !editingId}>
              {editingId ? 'Update Product' : 'Add Product'}
            </Button>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden shadow-elevation-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Product Name</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Category</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">OEM/Brand</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Model/SKU</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Licensing</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Margin</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Strategic</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">AI Tags</th>
                <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {products?.map((product) => (
                <tr key={product?.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-16 py-12 text-sm text-foreground font-medium">
                    <div>
                      <span>{product?.productName}</span>
                      {product?.catalogProductId && (
                        <span className="ml-6 inline-flex items-center gap-4 text-xs text-blue-500">
                          <Icon name="Link" size={10} />
                          Catalog
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-16 py-12">
                    <span className={`inline-flex items-center gap-4 px-8 py-4 rounded-md text-xs font-medium ${getCategoryColor(product?.category)}`}>
                      {product?.category}
                    </span>
                  </td>
                  <td className="px-16 py-12 text-sm text-muted-foreground">{product?.oemBrand}</td>
                  <td className="px-16 py-12 text-sm text-muted-foreground font-mono">{product?.modelSku}</td>
                  <td className="px-16 py-12 text-sm text-muted-foreground">{product?.licensingModel}</td>
                  <td className="px-16 py-12 text-sm text-muted-foreground capitalize">{product?.marginCategory}</td>
                  <td className="px-16 py-12">
                    <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium border ${getStrategicColor(product?.strategicMarking)}`}>
                      {STRATEGIC_OPTIONS?.find(s => s?.value === product?.strategicMarking)?.label || product?.strategicMarking}
                    </span>
                  </td>
                  <td className="px-16 py-12">
                    <div className="flex flex-wrap gap-4">
                      {product?.aiTags?.slice(0, 2)?.map((tag, i) => (
                        <span key={i} className="px-6 py-2 bg-primary/10 text-primary rounded text-xs">{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-16 py-12">
                    <div className="flex items-center justify-end gap-8">
                      <Button variant="ghost" size="icon" iconName="Edit" iconSize={16} onClick={() => handleEdit(product)} />
                      <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => onDeleteProduct(product?.id)} />
                    </div>
                  </td>
                </tr>
              ))}
              {(!products || products?.length === 0) && (
                <tr>
                  <td colSpan={9} className="px-16 py-32 text-center text-sm text-muted-foreground">
                    No products mapped yet. Add products from the catalog above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile cards */}
      <div className="lg:hidden space-y-12">
        {products?.map((product) => (
          <div key={product?.id} className="bg-card border border-border rounded-lg p-16 shadow-elevation-sm">
            <div className="flex items-start justify-between gap-12 mb-12">
              <div>
                <p className="font-semibold text-foreground text-sm">{product?.productName}</p>
                <p className="text-xs text-muted-foreground mt-2">{product?.oemBrand} · {product?.modelSku}</p>
              </div>
              <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium border ${getStrategicColor(product?.strategicMarking)}`}>
                {product?.strategicMarking}
              </span>
            </div>
            <div className="flex items-center gap-8 mt-12">
              <Button variant="outline" size="sm" iconName="Edit" iconSize={14} onClick={() => handleEdit(product)}>Edit</Button>
              <Button variant="ghost" size="sm" iconName="Trash2" iconSize={14} onClick={() => onDeleteProduct(product?.id)}>Remove</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductsTable;